import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Download, Mail } from 'lucide-react';
import { Botao, Cartao, Indicador, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { Chatter } from '../../componentes/chatter';
import { Drawer } from '../../componentes/sobreposicao';
import { LinkExportacao } from '../../componentes/discreto';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { get } from '../../nucleo/api';
import { dataHora, dinheiro, numero, nomePessoa } from '../../nucleo/formato';
import { rotuloCompetencia, type Competencia, type Holerite, type HoleriteDetalhe, type Natureza } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { anunciarOcultos } from '../../componentes/sensivel';

/** Baixa o PDF pela rota de anexos do núcleo (a sessão segue no cookie). */
function BaixarPdf({ anexoId, rotulo }: { anexoId: string; rotulo: string }) {
  return (
    <LinkExportacao
      href={`/api/v1/anexos/${anexoId}`}
      download
      onClick={(e) => e.stopPropagation()}
      className="inline-flex h-8 shrink-0 items-center justify-center gap-2 rounded-lg border border-borda bg-sup-2 px-3 text-sm font-medium text-tinta-1 hover:bg-sup-3"
    >
      <Download className="size-4" aria-hidden />
      {rotulo}
    </LinkExportacao>
  );
}

function referencia(i: HoleriteDetalhe['itens'][number]) {
  if (i.aliquota_bp) return `${numero(i.aliquota_bp / 100, 2)}%`;
  if (i.quantidade !== null) return numero(i.quantidade, 2);
  return '';
}

function DetalheHolerite({ id }: { id: string }) {
  const { t } = useTranslation('folha');
  const q = useQuery({ queryKey: ['folha', 'holerite', id], queryFn: () => get<HoleriteDetalhe>(`/folha/holerites/${id}`) });
  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  const h = q.data;
  const grupos: Natureza[] = ['provento', 'desconto', 'encargo'];
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <TextoCortado como="p" className="text-sm text-tinta-2">
            {h.funcao_nome ?? '-'} · {h.departamento_nome ?? '-'}
          </TextoCortado>
          <p className="text-xs text-tinta-3">{t('holerites.competenciaDe', { competencia: rotuloCompetencia(h.ano_mes) })}</p>
        </div>
        {h.anexo_id ? <BaixarPdf anexoId={h.anexo_id} rotulo={t('acoes.baixarPdf')} /> : <Selo cor="laranja">{t('holerites.semPdf')}</Selo>}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Indicador rotulo={t('totais.proventos')} valor={dinheiro(h.proventos_centavos, h.moeda)} cor="verde" />
        <Indicador rotulo={t('totais.descontos')} valor={dinheiro(h.descontos_centavos, h.moeda)} cor="laranja" />
        <Indicador rotulo={t('totais.liquido')} valor={dinheiro(h.liquido_centavos, h.moeda)} />
        <Indicador rotulo={t('totais.encargos')} valor={dinheiro(h.encargos_centavos, h.moeda)} cor="ciano" />
      </div>
      {grupos.map((g) => {
        const itens = h.itens.filter((i) => i.natureza === g);
        if (!itens.length) return null;
        return (
          <Cartao key={g} titulo={t(`naturezaPlural.${g}`)}>
            <ul className="divide-y divide-borda">
              {itens.map((i) => (
                <li key={i.id} className="flex min-w-0 items-center justify-between gap-3 py-2 text-sm">
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="numero w-14 shrink-0 text-xs text-tinta-3">{i.codigo}</span>
                    <span className="truncate" title={i.nome}>{i.nome}</span>
                  </span>
                  <span className="flex shrink-0 items-baseline gap-3">
                    <span className="numero text-xs text-tinta-3">{referencia(i)}</span>
                    <span className="numero">{anunciarOcultos(dinheiro(i.valor_centavos, h.moeda))}</span>
                  </span>
                </li>
              ))}
            </ul>
          </Cartao>
        );
      })}
      {h.email_enviado_em && (
        <p className="flex items-center gap-2 text-xs text-tinta-3">
          <Mail className="size-4" aria-hidden />
          {t('holerites.emailEnviado', { data: dataHora(h.email_enviado_em) })}
        </p>
      )}
      <Chatter entidade="folha.holerite" registroId={h.id} nomes={[h.funcionario_nome]} />
    </div>
  );
}

/** M8.4: holerites por competência e funcionário, com o PDF para baixar. */
export function Holerites() {
  const { t } = useTranslation('folha');
  const { t: tn } = useTranslation();
  const [busca, setBusca] = useSearchParams();
  const [detalhe, setDetalhe] = useState<{ id: string; nome: string } | null>(null);
  const competencias = useQuery({ queryKey: ['folha', 'competencias-com-holerite'], queryFn: () => get<{ itens: Competencia[] }>('/folha/competencias', { limite: 60 }) });

  if (competencias.isPending) return <Carregando />;
  if (competencias.isError) return <Erro erro={competencias.error} aoTentar={() => competencias.refetch()} />;
  const lista = competencias.data.itens.filter((c) => c.funcionarios > 0);
  if (lista.length === 0) return <Vazio texto={t('vazio.holerites')} />;
  const pedida = busca.get('competencia');
  const escolhida = lista.find((c) => c.id === pedida) ?? lista[0]!;

  const colunas: Coluna<Holerite>[] = [
    { chave: 'funcionario_nome', rotulo: t('campos.funcionario'), principal: true, render: (h) => <span className="font-medium">{nomePessoa(h.funcionario_nome)}</span> },
    { chave: 'funcao_nome', rotulo: t('campos.funcao'), render: (h) => h.funcao_nome ?? '-' },
    { chave: 'departamento_nome', rotulo: t('campos.departamento'), render: (h) => h.departamento_nome ?? '-' },
    { chave: 'proventos_centavos', rotulo: t('totais.proventos'), alinhar: 'direita', mono: true, render: (h) => dinheiro(h.proventos_centavos, h.moeda) },
    { chave: 'descontos_centavos', rotulo: t('totais.descontos'), alinhar: 'direita', mono: true, render: (h) => dinheiro(h.descontos_centavos, h.moeda) },
    { chave: 'liquido_centavos', rotulo: t('totais.liquido'), alinhar: 'direita', mono: true, render: (h) => <span className="font-medium">{anunciarOcultos(dinheiro(h.liquido_centavos, h.moeda))}</span> },
    { chave: 'pdf', rotulo: t('holerites.pdf'), render: (h) => (h.anexo_id ? <BaixarPdf anexoId={h.anexo_id} rotulo={t('acoes.baixarPdf')} /> : <span className="text-tinta-3">{t('holerites.semPdf')}</span>) },
  ];

  return (
    <>
      <Tabela<Holerite>
        tela="folha.holerites"
        caminho="/folha/holerites"
        params={{ competencia_id: escolhida.id }}
        colunas={colunas}
        aoClicar={(h) => setDetalhe({ id: h.id, nome: h.funcionario_nome })}
        filtros={{ competencia: escolhida.id }}
        aoAplicarFiltros={(f) => {
          if (f.competencia && lista.some((c) => c.id === f.competencia)) setBusca({ competencia: String(f.competencia) }, { replace: true });
        }}
        vazio={<Vazio texto={t('vazio.holerites')} />}
        filtrosExtra={
          <div className="w-full sm:w-56">
            <Selecao
              aria-label={t('campos.competencia')}
              opcoes={lista.map((c) => ({ valor: c.id, rotulo: `${rotuloCompetencia(c.ano_mes)} (${t(`estado.${c.estado}`)})` }))}
              value={escolhida.id}
              onChange={(e) => setBusca({ competencia: e.target.value }, { replace: true })}
            />
          </div>
        }
      />
      <Drawer aberto={!!detalhe} aoFechar={() => setDetalhe(null)} largura="lg" titulo={detalhe ? t('holerites.titulo', { nome: nomePessoa(detalhe.nome) }) : ''}
        rodape={
          <Botao variante="fantasma" onClick={() => setDetalhe(null)}>
            {tn('comum.fechar')}
          </Botao>
        }
      >
        {detalhe && <DetalheHolerite key={detalhe.id} id={detalhe.id} />}
      </Drawer>
    </>
  );
}
